import { useEffect, useRef, useState } from "react"
import { Client } from "@xmpp/client"
import { Check, ChevronDown } from "lucide-react"
import AdHocCommands from "@/api/adHocCommands"
import { parseNaviJson } from "@/lib/naviParser"
import {
  normalizeContractorQuery,
  normalizeNip,
  pickBestContractor,
} from "@/lib/contractorQuery"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { cn } from "@/lib/utils"
import { useTranslation } from "react-i18next"

export type Contractor = {
  Id: number
  ShortName?: string
  Name?: string
  TIN?: string
  HeadquartersAddressId?: number
}

// NIP obok nazwy, bo kilkaset firm w kartotece Marcosa ma nierozróżnialne
// nazwy — bez numeru nie da się wybrać właściwej.
export const contractorLabel = (c: Contractor): string => {
  const name = c.ShortName || c.Name
  if (!name) return `#${c.Id}`
  return c.TIN ? `${name} · ${c.TIN}` : name
}

// There are thousands of contractors, far too many to load into a select.
// Search them server-side by name as the dispatcher types.
export default function ContractorSearch({
  client,
  vhost,
  carrier,
  placeholder,
  searchPlaceholder,
  ariaLabel,
  selected,
  onSelect,
  onError,
  autoSelectQuery,
  autoSelectNip,
  limit,
}: {
  client: Client
  vhost: string
  carrier: boolean
  placeholder: string
  // Placeholder of the search box inside the popover. Without it, the box
  // derives one from `placeholder`.
  searchPlaceholder?: string
  ariaLabel: string
  selected?: Contractor
  onSelect: (c: Contractor | undefined) => void
  onError: () => void
  // Name from the order to default-select on mount (customer or recipient).
  autoSelectQuery?: string
  // NIP from the order, tried before the name (an exact match on a unique
  // id beats fuzzy name matching). Falls back to autoSelectQuery when no
  // contractor has this NIP in Marcos.
  autoSelectNip?: string
  // How many suggestions to ask Marcos for. Omitted means the server default
  // of 20 — the order panel relies on that; picking your own book of companies
  // asks for more.
  limit?: number
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [text, setText] = useState("")
  const [results, setResults] = useState<Contractor[]>([])
  const [loading, setLoading] = useState(false)
  // "not found" = we searched for autoSelectQuery but Marcos returned nothing,
  // so the dispatcher must add the contractor there first. Cleared once the
  // user picks anything manually.
  const [notFound, setNotFound] = useState(false)
  const seq = useRef(0)

  // Best-effort default selection from the order. Runs once per query; only
  // fills an empty dropdown and never overrides a manual choice. NIP is tried
  // first — it's an exact match on a unique id — and only when no contractor
  // has it do we fall back to the name search. The order's full legal name
  // ("LAUVJAH SPEDYCJA SP. Z O.O.") isn't a substring of the Marcos name
  // ("LAUVJAH SPEDYCJA"), so that fallback searches with the normalized core
  // and picks the best match from the results.
  useEffect(() => {
    if (selected) return
    const nip = normalizeNip(autoSelectNip)
    const q = normalizeContractorQuery(autoSelectQuery)
    if (!nip && !q) return
    let stale = false

    const byName = () => {
      if (!q) {
        setNotFound(true)
        return
      }
      AdHocCommands.getNaviContractors(client, vhost, {
        carrier,
        query: q,
        limit,
      })
        .then((res) => {
          if (stale) return
          const data = parseNaviJson(res) || {}
          const list: Contractor[] =
            (carrier ? data.carriers : data.customers) || []
          const match = pickBestContractor(list, autoSelectQuery)
          if (match) {
            setNotFound(false)
            onSelect(match)
          } else {
            setNotFound(true)
          }
        })
        .catch(() => onError())
    }

    if (nip) {
      AdHocCommands.getNaviContractors(client, vhost, { carrier, nip, limit })
        .then((res) => {
          if (stale) return
          const data = parseNaviJson(res) || {}
          const list: Contractor[] =
            (carrier ? data.carriers : data.customers) || []
          if (list.length > 0) {
            setNotFound(false)
            onSelect(list[0])
          } else {
            byName()
          }
        })
        .catch(() => onError())
    } else {
      byName()
    }

    return () => {
      stale = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, vhost, carrier, autoSelectQuery, autoSelectNip, limit])

  useEffect(() => {
    if (!open) return
    const q = text.trim()
    const id = ++seq.current
    setLoading(true)
    const timer = setTimeout(() => {
      AdHocCommands.getNaviContractors(client, vhost, {
        carrier,
        query: q || undefined,
        limit,
      })
        .then((res) => {
          if (id !== seq.current) return // ignore stale responses
          const data = parseNaviJson(res) || {}
          setResults((carrier ? data.carriers : data.customers) || [])
        })
        .catch(() => onError())
        .finally(() => {
          if (id === seq.current) setLoading(false)
        })
    }, 300)
    return () => clearTimeout(timer)
  }, [client, vhost, carrier, text, open, onError, limit])

  return (
    <div className="flex flex-col gap-1">
      <Popover open={open} onOpenChange={setOpen} modal={true}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-label={ariaLabel}
            aria-expanded={open}
            className="w-full justify-between px-3 font-normal"
          >
            <span
              className={cn("truncate", !selected && "text-muted-foreground")}
            >
              {selected ? contractorLabel(selected) : placeholder}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="z-[9999] w-[var(--radix-popover-trigger-width)] min-w-[240px] max-w-[90vw] p-0"
          sideOffset={5}
        >
          {/* Server-side search: disable cmdk's built-in client filtering. */}
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={
                searchPlaceholder ??
                t("orders.contractorSearch.searchPlaceholder", { placeholder })
              }
              value={text}
              onValueChange={setText}
            />
            <CommandList className="max-h-[300px] overflow-y-auto">
              <CommandEmpty>
                {loading
                  ? t("orders.contractorSearch.searching")
                  : t("orders.contractorSearch.noResults")}
              </CommandEmpty>
              <CommandGroup>
                {results.map((c) => (
                  <CommandItem
                    key={c.Id}
                    value={String(c.Id)}
                    onSelect={() => {
                      setNotFound(false)
                      onSelect(c)
                      setOpen(false)
                      setText("")
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selected?.Id === c.Id ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {contractorLabel(c)}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {notFound && !selected && autoSelectQuery && (
        <p className="text-xs text-amber-600 dark:text-amber-500">
          {carrier
            ? t("orders.contractorSearch.carrierNotFound", {
                name: autoSelectQuery.trim(),
              })
            : t("orders.contractorSearch.customerNotFound", {
                name: autoSelectQuery.trim(),
              })}
        </p>
      )}
    </div>
  )
}
